import { toCamelCaseObj } from '@utils';
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import { NodeAPIResponse } from '../types/api';
import { FreeTrialStatus } from '../types/freeTrialStatus';
import {
  AgreementPayload,
  AgreementResponseData,
  FreeTrialPayload,
  InitiatePaymentPayload,
  InitiatePaymentResponse,
  KraPayload,
  KraVerificationResponseData,
  RRiskProfileQuestionnaire,
  SubscriptionHistoryResponse,
  SubscriptionMeta,
  SubscriptionUserDetails,
  UpdatePaymentStatusPayload,
  UserPlan,
  saveUserDetailsPayload,
  uploadPdfPayload,
} from '../types/subscriptions';
import {
  intradayPaymentsEndpoints,
  intradayPaymentsFetcher,
  intradayPortfolioBusinessFetcher,
  intradaySubscriptionFetcher,
  intradaySubscriptionHistoryFetcher,
  intradaySubscriptionsEndpoints,
} from './fetchers';

export async function getUserSavedSubscriptionDetails(meta: {
  resourceId: string;
  product: string;
}) {
  try {
    const response = await intradaySubscriptionFetcher.get<
      NodeAPIResponse<SubscriptionUserDetails>
    >(
      intradaySubscriptionsEndpoints.getSavedDetails(
        meta.resourceId,
        meta?.product
      )
    );

    return toCamelCaseObj(response?.data?.data);
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function getUserAgreement(payload: AgreementPayload) {
  try {
    const response = await intradaySubscriptionFetcher.post<
      NodeAPIResponse<AgreementResponseData>
    >(intradaySubscriptionsEndpoints.getAgreement, payload);

    return toCamelCaseObj(response?.data?.data);
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function saveSubscriptionDetails(payload: saveUserDetailsPayload) {
  try {
    const response = await intradaySubscriptionFetcher.post<
      NodeAPIResponse<KraVerificationResponseData>
    >(intradaySubscriptionsEndpoints.saveDetails, payload);

    return response?.data?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function uploadAgreementPdf(payload: uploadPdfPayload) {
  try {
    const response = await intradaySubscriptionFetcher.post<
      NodeAPIResponse<KraVerificationResponseData>
    >(intradaySubscriptionsEndpoints.uploadAgreement, payload);

    return toCamelCaseObj(response?.data?.data);
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function verifyKra(payload: KraPayload) {
  try {
    const response = await intradaySubscriptionFetcher.post<
      NodeAPIResponse<KraVerificationResponseData>
    >(intradaySubscriptionsEndpoints.kraVerify, payload);

    return response?.data?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function getPlans(meta: SubscriptionMeta) {
  try {
    const response = await intradayPaymentsFetcher.post<
      NodeAPIResponse<UserPlan>
    >(intradayPaymentsEndpoints.getPlans(meta.clientId), meta);

    return response?.data?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function initiatePayment(
  args0: Omit<InitiatePaymentPayload, 'gwName'>
) {
  try {
    const response = await intradayPaymentsFetcher.post<
      NodeAPIResponse<InitiatePaymentResponse>
    >(intradayPaymentsEndpoints.initiatePayment, {
      gwName: 'razorpay',
      ...args0,
    });
    return response?.data?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function updatePaymentStatus(
  args0: Omit<UpdatePaymentStatusPayload, 'gwName'>
) {
  try {
    const response = await intradayPaymentsFetcher.post<
      NodeAPIResponse<undefined>
    >(intradayPaymentsEndpoints.updatePaymentStatus, {
      ...args0,
      gwName: 'razorpay',
    });
    return response?.data?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function getFreeTrialStatus(params: FreeTrialPayload) {
  try {
    const p = new URLSearchParams(params);
    const response = await intradayPaymentsFetcher.get<
      NodeAPIResponse<FreeTrialStatus>
    >(intradayPaymentsEndpoints.getFreeTrialStatus + `?${p.toString()}`);
    return response?.data?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function activeFreeTrial(payload: FreeTrialPayload) {
  try {
    const response = await intradayPaymentsFetcher.post<
      NodeAPIResponse<undefined>
    >(intradayPaymentsEndpoints.activeFreeTrial, payload);
    return response?.data;
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function getRiskQuestionnaire(meta: { analystId: string }) {
  try {
    const response =
      await intradayPortfolioBusinessFetcher.get<RRiskProfileQuestionnaire>(
        intradayPaymentsEndpoints.getRiskQuestionnaire(meta.analystId)
      );

    return toCamelCaseObj(response?.data);
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function getSubscriptionHistory(params: {
  userId: string;
  broker: string;
  type: 'all' | 'paid' | 'free';
  expiredActive: boolean;
}) {
  try {
    const response = await intradaySubscriptionHistoryFetcher.get<
      NodeAPIResponse<SubscriptionHistoryResponse>
    >(
      intradayPaymentsEndpoints.getSubscriptionHistory(
        params.userId,
        params.broker,
        params.type,
        params.expiredActive
      )
    );
    return response?.data?.data?.map((item) => toCamelCaseObj(item));
  } catch (e: any) {
    throw e?.response?.data;
  }
}

export async function downloadAgreement(params: {
  userId: string;
  resourceId: string;
  product: string;
}) {
  try {
    const response = await intradaySubscriptionFetcher.get<
      NodeAPIResponse<{ presigned_url: string }>
    >(
      intradaySubscriptionsEndpoints.downloadAgreement(
        params.userId,
        params.resourceId,
        params.product
      )
    );

    // Get the presigned URL from the response
    const presignedUrl = response.data.data.presigned_url;

    // Download the PDF using the presigned URL
    const pdfResponse = await fetch(presignedUrl);
    const pdfBlob = await pdfResponse.blob();

    // Generate filename with current date
    const currentDate = format(new Date(), 'yyyy-MM-dd');
    const fileName = `${currentDate}_termsofservice.pdf`;

    // Save the file
    saveAs(pdfBlob, fileName);
  } catch (e: any) {
    throw e?.response?.data;
  }
}
